import { IApiError } from '@frontend/request';

import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';

import priceAlertAPI from '../api';
import {
  IBulkActionResponse,
  IBulkDeletePriceAlertsParams,
  ICreatePriceAlertParams,
  IDeletePriceAlertParams,
  IGetPriceAlertsParams,
  IInitSymbolPriceAlerts,
  IPriceAlert,
  IPriceAlertConfig,
  IUpdatePriceAlertParams,
  defaultGetPriceAlertsParams
} from '../model';

class PriceAlertAPIController {
  private getPriceAlertsAC?: AbortController;
  private getPriceAlertsBySymbolAC?: AbortController;

  public getPriceAlerts = async (
    params: IGetPriceAlertsParams = defaultGetPriceAlertsParams,
    callbacks: IAPIControllerCallbacks<IInitSymbolPriceAlerts[]>
  ) => {
    this.getPriceAlertsAC?.abort();
    this.getPriceAlertsAC = new AbortController();

    try {
      const { result } = await priceAlertAPI.getAlerts(params, this.getPriceAlertsAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getPriceAlertsBySymbol = async (
    symbol: string,
    callbacks?: IAPIControllerCallbacks<IInitSymbolPriceAlerts>
  ) => {
    this.getPriceAlertsBySymbolAC?.abort();
    this.getPriceAlertsBySymbolAC = new AbortController();

    try {
      const { result } = await priceAlertAPI.getAlertsBySymbol(symbol, this.getPriceAlertsBySymbolAC.signal);
      callbacks?.onSuccess(result);
    } catch (error) {
      callbacks?.onError(error as IApiError);
    }
  };

  public createPriceAlert = async (
    params: ICreatePriceAlertParams,
    callbacks: IAPIControllerCallbacks<IPriceAlert>
  ) => {
    try {
      const { result } = await priceAlertAPI.createAlert(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public updatePriceAlert = async (
    params: IUpdatePriceAlertParams,
    callbacks: IAPIControllerCallbacks<IPriceAlert>
  ) => {
    try {
      const { result } = await priceAlertAPI.updateAlert(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public deletePriceAlert = async (params: IDeletePriceAlertParams, callbacks: IAPIControllerCallbacks<string>) => {
    try {
      const { result } = await priceAlertAPI.deleteAlert(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public bulkDeletePriceAlerts = async (
    params: IBulkDeletePriceAlertsParams,
    callbacks: IAPIControllerCallbacks<IBulkActionResponse>
  ) => {
    try {
      if (!params.ids || params.ids.length <= 0) {
        throw new Error('No price alerts selected. Please submit at least 1 price alert.');
      }

      const { result } = await priceAlertAPI.bulkDeleteAlerts(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getAlertConfig = async (callbacks: IAPIControllerCallbacks<IPriceAlertConfig>) => {
    try {
      const { result } = await priceAlertAPI.getAlertConfig();
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };
}

export const priceAlertAPIController = new PriceAlertAPIController();
